import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  UserIcon,
  PencilIcon,
  CameraIcon,
  CheckIcon,
  XMarkIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  CalendarIcon,
  TrashIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import StripeConnectSettings from '../components/StripeConnectSettings';

const Profile = () => {
  const { user, updateProfile, logout } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [showPhotoUpload, setShowPhotoUpload] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [stripeAvailable, setStripeAvailable] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    height: '',
    weight: '',
    position: '',
    jerseyNumber: '',
    bio: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    emergencyContact: '',
    emergencyPhone: '',
    medicalInfo: '',
    parentName: '',
    parentEmail: '',
    parentPhone: '',
    uniform_info: {
      jersey_number: '',
      sizing_type: '',
      hat_size: '',
      shirt_size: '',
      pant_size: ''
    },
    payment_preferences: {
      accepted_methods: ['stripe'],
      preferred_method: 'stripe'
    }
  });

  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');

  // Fetch user profile data
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await fetch('/api/auth/profile', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (response.ok) {
          const profileData = await response.json();
          setFormData({
            name: profileData.name || '',
            email: profileData.email || '',
            phone: profileData.phone || '',
            height: profileData.height || '',
            weight: profileData.weight || '',
            position: profileData.position || '',
            jerseyNumber: profileData.jerseyNumber || '',
            bio: profileData.bio || '',
            address: profileData.address || '',
            city: profileData.city || '',
            state: profileData.state || '',
            zipCode: profileData.zipCode || '',
            emergencyContact: profileData.emergencyContact || '',
            emergencyPhone: profileData.emergencyPhone || '',
            medicalInfo: profileData.medicalInfo || '',
            parentName: profileData.parentName || '',
            parentEmail: profileData.parentEmail || '',
            parentPhone: profileData.parentPhone || '',
            uniform_info: profileData.uniform_info || {
              jersey_number: '',
              sizing_type: '',
              hat_size: '',
              shirt_size: '',
              pant_size: ''
            },
            payment_preferences: profileData.payment_preferences || {
              accepted_methods: ['stripe'],
              preferred_method: 'stripe'
            }
          });
          setPhotoPreview(profileData.profilePicture || '');
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
        // Fallback to user data from context
        if (user) {
          setFormData({
            name: user.name || '',
            email: user.email || '',
            phone: user.phone || '',
            height: user.height || '',
            weight: user.weight || '',
            position: user.position || '',
            jerseyNumber: user.jerseyNumber || '',
            bio: user.bio || '',
            address: user.address || '',
            city: user.city || '',
            state: user.state || '',
            zipCode: user.zipCode || '',
            emergencyContact: user.emergencyContact || '',
            emergencyPhone: user.emergencyPhone || '',
            medicalInfo: user.medicalInfo || '',
            parentName: user.parentName || '',
            parentEmail: user.parentEmail || '',
            parentPhone: user.parentPhone || '',
          });
          setPhotoPreview(user.profilePicture || '');
        }
      }
    };

    fetchProfile();
  }, [user]);

  useEffect(() => {
    const fetchStripeAvailability = async () => {
      try {
        const response = await fetch('/api/stripe/availability', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setStripeAvailable(!!data.stripeAvailable);
        } else {
          setStripeAvailable(false);
        }
      } catch (error) {
        console.error('Error fetching Stripe availability:', error);
        setStripeAvailable(false);
      }
    };

    fetchStripeAvailability();
  }, []);

  useEffect(() => {
    setFormData(prev => {
      const availableMethods = new Set([
        ...(stripeAvailable ? ['stripe'] : [])
      ]);

      const filteredAccepted = (prev.payment_preferences.accepted_methods || [])
        .filter(method => availableMethods.has(method));

      let accepted_methods = filteredAccepted.length
        ? filteredAccepted
        : [Array.from(availableMethods)[0]];

      if (stripeAvailable && prev.payment_preferences.accepted_methods?.includes('stripe') && !accepted_methods.includes('stripe')) {
        accepted_methods = [...accepted_methods, 'stripe'];
      }

      const preferred_method = accepted_methods.includes(prev.payment_preferences.preferred_method)
        ? prev.payment_preferences.preferred_method
        : accepted_methods[0];

      return {
        ...prev,
        payment_preferences: {
          ...prev.payment_preferences,
          accepted_methods,
          preferred_method
        }
      };
    });
  }, [stripeAvailable]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Handle nested objects like uniform_info
    if (name.startsWith('uniform_info.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        uniform_info: {
          ...prev.uniform_info,
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const formDataToSend = new FormData();
      
      Object.entries(formData).forEach(([key, value]) => {
        if (value === undefined || value === null) {
          return;
        }

        if (typeof value === 'object') {
          formDataToSend.append(key, JSON.stringify(value));
        } else {
          formDataToSend.append(key, value);
        }
      });

      // Add photo if selected
      if (photoFile) {
        formDataToSend.append('profilePicture', photoFile);
      }

      const result = await updateProfile(formDataToSend);

      if (result.success) {
        const updatedProfile = result.data || {};
        setFormData(prev => ({
          ...prev,
          ...updatedProfile,
          payment_preferences: updatedProfile.payment_preferences || prev.payment_preferences,
          uniform_info: updatedProfile.uniform_info || prev.uniform_info
        }));
        setSuccess('Profile updated successfully!');
      setIsEditing(false);
      setPhotoFile(null);
      } else {
        setError(result.error || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setError('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setPhotoFile(null);
    setPhotoPreview(user?.profilePicture || '');
    setError('');
    setSuccess('');
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      setDeleteError('Please enter your password to confirm.');
      return;
    }
    setDeleteLoading(true);
    setDeleteError('');
    try {
      const response = await fetch('/api/auth/profile', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password: deletePassword }),
      });
      const data = await response.json();
      if (!response.ok) {
        setDeleteError(data.error || 'Failed to delete account.');
        return;
      }
      logout();
    } catch {
      setDeleteError('An error occurred. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not provided';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <UserIcon className="h-8 w-8 text-blue-600 mr-3" />
            Profile
          </h1>
          {!isEditing && (
          <button
              onClick={() => setIsEditing(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
            >
              <PencilIcon className="h-5 w-5 mr-2" />
              Edit Profile
            </button>
          )}
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex">
            <CheckIcon className="h-5 w-5 text-green-400" />
            <p className="ml-3 text-sm text-green-700">{success}</p>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <XMarkIcon className="h-5 w-5 text-red-400" />
            <p className="ml-3 text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Profile Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h2>
          
          <div className="flex items-start space-x-6">
            {/* Profile Photo */}
            <div className="flex-shrink-0">
            <div className="relative">
                <div className="h-24 w-24 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
              {photoPreview ? (
                <img
                  src={photoPreview}
                  alt="Profile"
                      className="h-full w-full object-cover"
                />
              ) : (
                  <UserIcon className="h-12 w-12 text-gray-400" />
                  )}
                </div>
              {isEditing && (
                  <button
                    type="button"
                    onClick={() => setShowPhotoUpload(!showPhotoUpload)}
                    className="absolute -bottom-1 -right-1 bg-blue-600 text-white rounded-full p-1 hover:bg-blue-700"
                  >
                    <CameraIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
              {showPhotoUpload && isEditing && (
                <div className="mt-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    className="text-sm text-gray-600"
                  />
                </div>
              )}
            </div>
            
            {/* Basic Info Fields */}
            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name
                </label>
                {isEditing ? (
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
                ) : (
                  <p className="text-gray-900">{formData.name || 'Not provided'}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                {isEditing ? (
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  />
                ) : (
                  <p className="text-gray-900 flex items-center">
                    <EnvelopeIcon className="h-4 w-4 mr-2 text-gray-400" />
                    {formData.email || 'Not provided'}
                  </p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                {isEditing ? (
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900 flex items-center">
                    <PhoneIcon className="h-4 w-4 mr-2 text-gray-400" />
                    {formData.phone || 'Not provided'}
                  </p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bio
                </label>
                {isEditing ? (
                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={handleChange}
                  rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Tell us about yourself..."
                />
                ) : (
                  <p className="text-gray-900">{formData.bio || 'Not provided'}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Physical Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Physical Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Height
              </label>
              {isEditing ? (
                <input
                  type="text"
                  name="height"
                  value={formData.height}
                  onChange={handleChange}
                  placeholder="e.g., 5'10&quot;"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              ) : (
                <p className="text-gray-900">{formData.height || 'Not provided'}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Weight
              </label>
              {isEditing ? (
                <input
                  type="text"
                  name="weight"
                  value={formData.weight}
                  onChange={handleChange}
                  placeholder="e.g., 150 lbs"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              ) : (
                <p className="text-gray-900">{formData.weight || 'Not provided'}</p>
              )}
            </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Position
                  </label>
              {isEditing ? (
                <input
                  type="text"
                    name="position"
                    value={formData.position}
                    onChange={handleChange}
                  placeholder="e.g., Pitcher, Shortstop"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              ) : (
                <p className="text-gray-900">{formData.position || 'Not provided'}</p>
              )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Jersey Number
                  </label>
              {isEditing ? (
                  <input
                  type="text"
                    name="jerseyNumber"
                    value={formData.jerseyNumber}
                    onChange={handleChange}
                    placeholder="e.g., 12"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              ) : (
                <p className="text-gray-900">{formData.jerseyNumber || 'Not provided'}</p>
              )}
            </div>
          </div>
        </div>

        {/* Uniform Information (for players and parents) */}
        {(user?.role === 'player' || user?.role === 'parent') && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Uniform Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Jersey Number
                </label>
                {isEditing ? (
                  <input
                    type="number"
                    name="uniform_info.jersey_number"
                    value={formData.uniform_info.jersey_number}
                    onChange={handleChange}
                    placeholder="e.g., 12"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900">{formData.uniform_info.jersey_number || 'Not provided'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sizing Type
                </label>
                {isEditing ? (
                  <select
                    name="uniform_info.sizing_type"
                    value={formData.uniform_info.sizing_type}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select sizing type</option>
                    <option value="Adult">Adult</option>
                    <option value="Child">Child</option>
                  </select>
                ) : (
                  <p className="text-gray-900">{formData.uniform_info.sizing_type || 'Not provided'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hat Size
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    name="uniform_info.hat_size"
                    value={formData.uniform_info.hat_size}
                    onChange={handleChange}
                    placeholder="e.g., 7 1/4, Large"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900">{formData.uniform_info.hat_size || 'Not provided'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Shirt Size
                </label>
                {isEditing ? (
                  <select
                    name="uniform_info.shirt_size"
                    value={formData.uniform_info.shirt_size}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select shirt size</option>
                    <option value="SM">SM</option>
                    <option value="MD">MD</option>
                    <option value="L">L</option>
                    <option value="XL">XL</option>
                    <option value="XXL">XXL</option>
                  </select>
                ) : (
                  <p className="text-gray-900">{formData.uniform_info.shirt_size || 'Not provided'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pant Size
                </label>
                {isEditing ? (
                  <select
                    name="uniform_info.pant_size"
                    value={formData.uniform_info.pant_size}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select pant size</option>
                    <option value="SM">SM</option>
                    <option value="MD">MD</option>
                    <option value="L">L</option>
                    <option value="XL">XL</option>
                    <option value="XXL">XXL</option>
                  </select>
                ) : (
                  <p className="text-gray-900">{formData.uniform_info.pant_size || 'Not provided'}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Address Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Address Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Street Address
              </label>
              {isEditing ? (
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              ) : (
                <p className="text-gray-900 flex items-center">
                  <MapPinIcon className="h-4 w-4 mr-2 text-gray-400" />
                  {formData.address || 'Not provided'}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City
              </label>
              {isEditing ? (
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              ) : (
                <p className="text-gray-900">{formData.city || 'Not provided'}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                State
              </label>
              {isEditing ? (
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              ) : (
                <p className="text-gray-900">{formData.state || 'Not provided'}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ZIP Code
              </label>
              {isEditing ? (
                <input
                  type="text"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              ) : (
                <p className="text-gray-900">{formData.zipCode || 'Not provided'}</p>
              )}
            </div>
          </div>
        </div>

        {/* Emergency Contact */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Emergency Contact</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Emergency Contact Name
              </label>
              {isEditing ? (
                <input
                  type="text"
                  name="emergencyContact"
                  value={formData.emergencyContact}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              ) : (
                <p className="text-gray-900">{formData.emergencyContact || 'Not provided'}</p>
              )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                Emergency Contact Phone
              </label>
              {isEditing ? (
                <input
                  type="tel"
                  name="emergencyPhone"
                  value={formData.emergencyPhone}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              ) : (
                <p className="text-gray-900">{formData.emergencyPhone || 'Not provided'}</p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Medical Information
              </label>
              {isEditing ? (
                <textarea
                  name="medicalInfo"
                  value={formData.medicalInfo}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Allergies, medications, medical conditions..."
                />
              ) : (
                <p className="text-gray-900">{formData.medicalInfo || 'Not provided'}</p>
              )}
            </div>
          </div>
        </div>

        {/* Parent Information (for players) */}
        {user?.role === 'player' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Parent Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Parent Name
                  </label>
                {isEditing ? (
                  <input
                    type="text"
                    name="parentName"
                    value={formData.parentName}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900">{formData.parentName || 'Not provided'}</p>
                )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                  Parent Email
                  </label>
                {isEditing ? (
                  <input
                    type="email"
                    name="parentEmail"
                    value={formData.parentEmail}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900">{formData.parentEmail || 'Not provided'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Parent Phone
                </label>
                {isEditing ? (
                  <input
                    type="tel"
                    name="parentPhone"
                    value={formData.parentPhone}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900">{formData.parentPhone || 'Not provided'}</p>
                )}
                </div>
              </div>
            </div>
          )}

        {/* Payment Preferences */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Payment Preferences</h2>
          <p className="text-sm text-gray-600 mb-4">Set up your preferred payment methods for receiving payments from invoices.</p>
          
          {stripeAvailable ? (
            <div className="flex items-center space-x-2">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                Stripe (Card) enabled
              </span>
            </div>
          ) : (
            <p className="text-sm text-gray-500">
              Stripe will appear here once your organization or team connects a Stripe account.
            </p>
          )}
        </div>

          {/* Account Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Account Information</h2>
          
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role
                </label>
              <p className="text-gray-900 capitalize">{user?.role?.replace('_', ' ') || 'Not provided'}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Member Since
                </label>
              <p className="text-gray-900 flex items-center">
                <CalendarIcon className="h-4 w-4 mr-2 text-gray-400" />
                {formatDate(user?.createdAt)}
              </p>
            </div>
            </div>
          </div>

        {/* Form Actions */}
          {isEditing && (
          <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleCancel}
              className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
              disabled={loading}
              className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
              {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}
        </form>

        {/* Payment Integrations — outside the form so their own buttons don't trigger a profile save */}
        {(user?.role === 'coach' || user?.role === 'assistant_coach' || user?.role === 'admin' || user?.role === 'org_admin') && (
          <div className="space-y-6">
            <StripeConnectSettings />
            {/* Venmo is turned off for now (server VENMO_ENABLED); all payments go through Stripe.
                To bring it back, re-add <VenmoAccountSettings /> from components/VenmoAccountSettings. */}
          </div>
        )}

        {/* Delete Account */}
        <div className="bg-white rounded-lg shadow-sm border border-red-200 p-6">
          <h2 className="text-lg font-medium text-red-700 mb-1 flex items-center">
            <TrashIcon className="h-5 w-5 mr-2" />
            Delete Account
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            Permanently delete your account and all associated data. This action cannot be undone.
          </p>
          <button
            type="button"
            onClick={() => { setShowDeleteModal(true); setDeleteError(''); setDeletePassword(''); }}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
          >
            Delete My Account
          </button>
        </div>

        {/* Delete Account Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
              <div className="flex items-center mb-4">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600 mr-3 shrink-0" />
                <h3 className="text-lg font-semibold text-gray-900">Delete Account</h3>
              </div>
              <p className="text-sm text-gray-600 mb-2">
                This will permanently delete your account and all associated data, including your profile, team memberships, messages, and analysis history.
              </p>
              <p className="text-sm font-medium text-gray-800 mb-4">This cannot be undone.</p>

              <label className="block text-sm font-medium text-gray-700 mb-1">
                Enter your password to confirm
              </label>
              <input
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                placeholder="Your current password"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent mb-3"
                onKeyDown={(e) => e.key === 'Enter' && handleDeleteAccount()}
              />

              {deleteError && (
                <p className="text-sm text-red-600 mb-3">{deleteError}</p>
              )}

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deleteLoading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteAccount}
                  disabled={deleteLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {deleteLoading ? 'Deleting...' : 'Delete My Account'}
                </button>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default Profile;
